// File: frontend/src/components/auth/OTPInput.js

import React, { useState, useRef, useEffect } from 'react';
import styles from './OTPInput.module.css';

const OTPInput = ({ 
  length = 7,
  value = '', 
  onChange, 
  autoFocus = false,
  disabled = false
}) => {
  const [otp, setOTP] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);
  const hiddenInputRef = useRef(null);
  
  // Initialize refs
  useEffect(() => {
    inputRefs.current = Array(length)
      .fill(null)
      .map((_, i) => inputRefs.current[i] || React.createRef());
  }, [length]);
  
  // Auto-focus first input
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]?.current) {
      setTimeout(() => {
        inputRefs.current[0].current?.focus();
      }, 100);
    }
  }, [autoFocus]);
  
  // Sync external value with internal state
  useEffect(() => {
    if (value) {
      const otpArray = value.split('').slice(0, length);
      setOTP([...otpArray, ...Array(length - otpArray.length).fill('')]);
    } else {
      setOTP(Array(length).fill(''));
    }
  }, [value, length]);
  
  // Handle iOS autofill from hidden input
  const handleHiddenInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    
    if (value.length > 0) {
      const newOTP = value.slice(0, length).split('');
      const paddedOTP = [...newOTP, ...Array(length - newOTP.length).fill('')];
      
      setOTP(paddedOTP);
      onChange(paddedOTP.join(''));
      
      const lastIndex = Math.min(newOTP.length - 1, length - 1);
      setTimeout(() => {
        inputRefs.current[lastIndex]?.current?.focus();
      }, 0);
      
      e.target.value = '';
    }
  };
  
  const handleChange = (e, index) => {
    const { value } = e.target;
    
    if (!/^\d*$/.test(value)) return;
    
    const newOTP = [...otp];
    
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, length);
      
      for (let i = 0; i < digits.length; i++) {
        if (index + i < length) {
          newOTP[index + i] = digits[i];
        }
      }
      
      setOTP(newOTP);
      onChange(newOTP.join(''));
      
      const nextIndex = Math.min(index + digits.length, length - 1);
      setTimeout(() => {
        inputRefs.current[nextIndex]?.current?.focus();
      }, 0);
    } else {
      newOTP[index] = value;
      setOTP(newOTP);
      onChange(newOTP.join(''));
      
      if (value && index < length - 1) {
        setTimeout(() => {
          inputRefs.current[index + 1]?.current?.focus();
        }, 0);
      }
    }
  };
  
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOTP = [...otp];
      
      if (otp[index] === '') {
        if (index > 0) {
          newOTP[index - 1] = '';
          setOTP(newOTP);
          onChange(newOTP.join(''));
          inputRefs.current[index - 1]?.current?.focus();
        }
      } else {
        newOTP[index] = '';
        setOTP(newOTP);
        onChange(newOTP.join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.current?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.current?.focus();
    } else if (e.key === 'Delete') {
      e.preventDefault();
      const newOTP = [...otp];
      newOTP[index] = '';
      setOTP(newOTP);
      onChange(newOTP.join(''));
    }
  };
  
  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    const digits = pastedData.replace(/\D/g, '');
    
    if (digits.length === 0) return;
    
    const newOTP = [...otp];
    
    for (let i = 0; i < digits.length && index + i < length; i++) {
      newOTP[index + i] = digits[i];
    }
    
    setOTP(newOTP);
    onChange(newOTP.join(''));
    
    const nextIndex = Math.min(index + digits.length, length - 1);
    setTimeout(() => {
      inputRefs.current[nextIndex]?.current?.focus();
    }, 0);
  };
  
  const handleFocus = (e) => {
    e.target.select();
  };
  
  const handleInput = (e, index) => {
    const value = e.target.value;
    if (value.length > 1) {
      handleChange(e, index);
    }
  };
  
  return (
    <div className={styles.otpContainer}>
      <input
        ref={hiddenInputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        onChange={handleHiddenInputChange}
        className={styles.hiddenInput}
        tabIndex={-1}
        aria-hidden="true"
      />
      
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            if (!inputRefs.current[index]) {
              inputRefs.current[index] = { current: null };
            }
            inputRefs.current[index].current = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={length}
          value={digit}
          onChange={(e) => handleChange(e, index)}
          onInput={(e) => handleInput(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={(e) => handlePaste(e, index)}
          onFocus={handleFocus}
          disabled={disabled}
          className={styles.otpInput}
          autoComplete="off"
          aria-label={`Digit ${index + 1} of ${length}`}
        />
      ))}
    </div>
  );
};

export default OTPInput;